import React, { useMemo } from 'react'
import { Box, Card, CardContent, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material'
import { People, Place } from '@mui/icons-material'
import moment from 'moment'
import 'moment/locale/fr'
import useTitles from 'common/hooks/useTitles'
import useFetch from 'common/hooks/useFetch'
import ApplicationConf from 'conf/ApplicationConf'
import { STATUS } from 'common/constants/storeConstants'

moment.locale('fr')

const formatRange = (event) => {
    if (event.allDay) {
        return 'Toute la journée'
    }
    const start = moment(event.start).format('HH:mm')
    const end = event.end ? moment(event.end).format('HH:mm') : ''
    return end ? `${start} - ${end}` : start
}

const AgendaApp = () => {
    useTitles(() => [{
        label: 'Agenda',
        href: '/agenda',
    }], [])

    const { data: events = [], status } = useFetch(ApplicationConf.agenda.events())

    const eventsByDay = useMemo(() => {
        const sorted = [...events]
            .filter(e => e.start)
            .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf())
        return sorted.reduce((acc, event) => {
            const dayKey = moment(event.start).format('YYYY-MM-DD')
            if (!acc[dayKey]) {
                acc[dayKey] = []
            }
            acc[dayKey].push(event)
            return acc
        }, {})
    }, [events])

    if (status === STATUS.LOADING) {
        return (
            <Box display='flex' justifyContent='center' padding={4}>
                <CircularProgress />
            </Box>
        )
    }

    const days = Object.keys(eventsByDay)

    return (
        <Box>
            <Card>
                <CardContent>
                    {days.length === 0 ? (
                        <Typography color='textSecondary'>Aucun évènement à venir</Typography>
                    ) : days.map(day => (
                        <Box key={day} marginBottom={2}>
                            <Typography variant='subtitle1' fontWeight='bold' sx={{ textTransform: 'capitalize' }}>
                                {moment(day).format('dddd D MMMM YYYY')}
                            </Typography>
                            <Divider sx={{ marginBottom: 1 }} />
                            <Stack spacing={1}>
                                {eventsByDay[day].map(event => (
                                    <Card key={event.id} variant='outlined' sx={{ padding: 1 }}>
                                        <Stack direction='row' spacing={2} alignItems='flex-start'>
                                            <Chip label={formatRange(event)} size='small' color='primary' />
                                            <Box flex={1}>
                                                <Typography fontWeight='bold'>{event.title}</Typography>
                                                {event.location && (
                                                    <Typography variant='body2' color='textSecondary'>
                                                        <Place fontSize='inherit' /> {event.location}
                                                    </Typography>
                                                )}
                                                {event.attendees?.length > 0 && (
                                                    <Typography variant='body2' color='textSecondary'>
                                                        <People fontSize='inherit' /> {event.attendees.join(', ')}
                                                    </Typography>
                                                )}
                                                {event.description && (
                                                    <Typography variant='body2' sx={{ whiteSpace: 'pre-line', marginTop: 0.5 }}>
                                                        {event.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Card>
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </CardContent>
            </Card>
        </Box>
    )
}

export default AgendaApp
